<template>
  <v-form v-model="isValid">
    <v-container class="py-0">
      <v-row>
        <v-col
          cols="12"
          md="12"
        >
          <infinity-name-field
            v-model="editedApp.Name"
            color="primary"
            label="Name of your app"
            hint="The name should be alphanumerical"
            :readonly="!editedApp.Editable"
          />
        </v-col>
        <v-col
          v-if="!isNew"
          cols="12"
          md="12"
        >
          <v-text-field
            color="purple"
            readonly
            label="APP URL"
            :value="appUrl"
            class="mt-n4"
            dense
          >
            <template
              v-if="originalApp.State === 'AppRunning'"
              v-slot:append
            >
              <v-btn
                icon
                :href="appUrl"
                target="_blank"
              >
                <v-icon dark>
                  mdi-open-in-new
                </v-icon>
              </v-btn>
            </template>            
          </v-text-field>
        </v-col>
        <v-col
          v-if="!isNew && originalApp.State === 'AppRunning' && originalApp.AppType.toLowerCase() === 'api'"
          cols="12"
          md="12"
        >
          <v-text-field
            color="purple"
            readonly
            label="Web URL"
            :value="`${appUrl}/web/`"
            class="mt-n4"
            dense
          >
            <template
              v-slot:append
            >
              <v-btn
                icon
                :href="`${appUrl}/web/`"
                target="_blank"
              >
                <v-icon dark>
                  mdi-open-in-new
                </v-icon>
              </v-btn>
            </template>            
          </v-text-field>
        </v-col>
        <v-col
          cols="12"
          md="3"
        >
          <infinity-select
            v-model="editedApp.AppLanguage"
            color="primary"
            label="Language"
            hint="Language of the application"
            :items="appLanguages"
            :readonly="!editedApp.Editable"
            :disabled="!appLanguages.length"
          />
        </v-col>
        <v-col
          cols="12"
          md="3"
        >
          <infinity-select
            v-model="editedApp.AppType"
            color="primary"
            label="Type"
            hint="Type of the application"
            :items="appTypes"
            :readonly="!editedApp.Editable"
          />
        </v-col>
        <v-col
          cols="12"
          md="3"
        >
          <infinity-select
            v-model="editedApp.AppVersion"
            color="primary"
            label="Version"
            hint="Version of the language"
            :items="appVersions"
            :readonly="!editedApp.Editable"
            :disabled="!appLanguages.length"
          />
        </v-col>
        <v-col
          cols="12"
          md="3"
        >
          <infinity-select
            v-model="editedApp.UniverseName"
            color="primary"
            label="Universe"
            hint="The universe it belongs"
            :items="universes"
            :readonly="!editedApp.Editable"
          />
        </v-col>

        <v-col
          cols="12"
          md="3"
        >
          <infinity-text-field
            v-model="editedApp.Port"
            type="number"
            color="primary"
            label="Port"
            hint="Used port"
            :readonly="!editedApp.Editable"
          />
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <infinity-text-field
            v-model="editedApp.GitServer"
            color="primary"
            label="Git Url"
            hint="Git service where the code is stored"
            :readonly="!editedApp.Editable"
          />
        </v-col>

        <v-col
          cols="12"
          md="3"
        >
          <infinity-text-field
            v-model="editedApp.Uuid"
            color="primary"
            label="Project name"
            hint="The name of the DevOps project"
            :readonly="!editedApp.Editable"
          />
        </v-col>

        <v-col
          cols="12"
          md="3"
        >
          <infinity-text-field
            v-model="editedApp.Branch"
            color="primary"
            label="Branch"
            hint="The branch used by the app"
            :readonly="!editedApp.Editable"
          />
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <infinity-text-field
            v-model="editedApp.Repository"
            color="primary"
            label="Repository"
            hint="The name of the git repository"
            :readonly="!editedApp.Editable"
          />
        </v-col>
        <v-col
          v-if="editedApp.AppType && editedApp.AppType != 'Freestyle'"
          cols="12"
          md="3"
        >
          <infinity-text-field
            v-model="editedApp.ModuleName"
            color="primary"
            :label="editedApp.AppLanguage === 'Python' ? 'Module name' : 'Package name'"
            :hint="editedApp.AppLanguage === 'Python' ? 'Python module name' : 'R package name'"
            :readonly="!editedApp.Editable"
          />
        </v-col>

        <v-col
          cols="12"
          md="3"
        >
          <infinity-text-field
            v-model="editedApp.Replicas"
            type="number"
            color="primary"
            label="Replicas"
            hint="Number of replicas"
            :readonly="!editedApp.Editable"
          />
        </v-col>

        <v-col
          v-if="editedApp.AppType && editedApp.AppType == 'API'"
          cols="12"
        >
          <infinity-text-field
            v-model="editedApp.ApiKey"
            color="primary"
            label="API key"
            hint="Authentication for API"
            :readonly="!editedApp.Editable"
          />
        </v-col>

        <v-col cols="12">
          <infinity-textarea
            v-model="editedApp.Description"
            color="secondary"
            label="Description"
            hint="Short summary of the app"
            :readonly="!editedApp.Editable"
          />
        </v-col>       

        <v-col 
          v-if="editedApp.Editable"
          cols="12"
        >
          <v-data-table
            :headers="[{text: 'Source', value: 'source'},{text: 'Target', value: 'target'}, { text: 'Actions', value: 'actions', sortable: false },]"
            :items="storages"
            sort-by="Source"
            class="elevation-1"
            hide-default-footer
          >
            <template v-slot:top>
              <v-toolbar
                flat
              >
                <v-toolbar-title>Storage</v-toolbar-title>
                <v-divider
                  class="mx-4"
                  inset
                  vertical
                />
                <v-spacer />
                <v-dialog
                  v-model="storageDialog"
                  max-width="500px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      color="primary"
                      dark
                      icon
                      class="mb-2"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon 
                        dark
                        color="primary"
                      >
                        mdi-plus
                      </v-icon>
                    </v-btn>
                  </template>
                  <v-card>
                    <v-card-title>
                      <span class="text-h5">{{ storageVarformTitle }}</span>
                    </v-card-title>

                    <v-card-text>
                      <v-container>
                        <v-row>
                          <v-col
                            cols="12"
                            sm="6"
                            md="6"
                          >
                            <infinity-select
                              v-model="editedStorageVar.source"
                              :items="availableStorages"
                              item-value="Name"
                              item-text="Name"
                              color="primary"
                              label="Source"
                            />
                          </v-col>
                          <v-col
                            cols="12"
                            sm="6"
                            md="6"
                          >
                            <v-text-field
                              v-model="editedStorageVar.target"
                              label="Target"
                            />
                          </v-col>                                                                            
                        </v-row>
                      </v-container>
                    </v-card-text>

                    <v-card-actions>
                      <v-spacer />
                      <v-btn
                        color="blue darken-1"
                        text
                        @click="closeStorage"
                      >
                        Cancel
                      </v-btn>
                      <v-btn
                        color="blue darken-1"
                        text
                        @click="saveStorage"
                      >
                        Save
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
                <v-dialog 
                  v-model="storageDialogDelete" 
                  max-width="500px"
                >
                  <v-card>
                    <v-card-title class="text-h5">
                      Are you sure you want to delete this storage?
                    </v-card-title>
                    <v-card-actions>
                      <v-spacer />
                      <v-btn 
                        color="blue darken-1" 
                        text 
                        @click="closeDeleteStorage"
                      >
                        Cancel
                      </v-btn>
                      <v-btn 
                        color="blue darken-1" 
                        text 
                        @click="deleteStorageItemConfirm"
                      >
                        OK
                      </v-btn>
                      <v-spacer />
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-toolbar>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-icon
                small
                class="mr-2"
                @click="editStorageItem(item)"
              >
                mdi-pencil
              </v-icon>
              <v-icon
                small
                @click="deleteStorageItem(item)"
              >
                mdi-delete
              </v-icon>
            </template>
            <template v-slot:no-data>
              <span>No environment variable configured</span>
            </template>
          </v-data-table>          
        </v-col>
        
        <v-col 
          v-if="editedApp.Editable"
          cols="12"
        >
          <v-data-table
            :headers="[{text: 'Name', value: 'name'},{text: 'Value', value: 'value'}, { text: 'Actions', value: 'actions', sortable: false },]"
            :items="envVars"
            sort-by="Name"
            class="elevation-1"
            hide-default-footer
          >
            <template v-slot:top>
              <v-toolbar
                flat
              >
                <v-toolbar-title>Environment Variables</v-toolbar-title>
                <v-divider
                  class="mx-4"
                  inset
                  vertical
                />
                <v-spacer />
                <v-dialog
                  v-model="envDialog"
                  max-width="500px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      color="primary"
                      dark
                      icon
                      class="mb-2"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon 
                        dark
                        color="primary"
                      >
                        mdi-plus
                      </v-icon>
                    </v-btn>
                  </template>
                  <v-card>
                    <v-card-title>
                      <span class="text-h5">{{ envVarformTitle }}</span>
                    </v-card-title>

                    <v-card-text>
                      <v-container>
                        <v-row>
                          <v-col
                            cols="12"
                            sm="6"
                            md="6"
                          >
                            <v-text-field
                              v-model="editedEnvVar.name"
                              label="Name"
                            />
                          </v-col>
                          <v-col
                            cols="12"
                            sm="6"
                            md="6"
                          >
                            <v-text-field
                              v-model="editedEnvVar.value"
                              label="Value"
                            />
                          </v-col>                                                                            
                        </v-row>
                      </v-container>
                    </v-card-text>

                    <v-card-actions>
                      <v-spacer />
                      <v-btn
                        color="blue darken-1"
                        text
                        @click="closeEnv"
                      >
                        Cancel
                      </v-btn>
                      <v-btn
                        color="blue darken-1"
                        text
                        @click="saveEnv"
                      >
                        Save
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
                <v-dialog 
                  v-model="envDialogDelete" 
                  max-width="500px"
                >
                  <v-card>
                    <v-card-title class="text-h5">
                      Are you sure you want to delete this variable?
                    </v-card-title>
                    <v-card-actions>
                      <v-spacer />
                      <v-btn 
                        color="blue darken-1" 
                        text 
                        @click="closeDeleteEnv"
                      >
                        Cancel
                      </v-btn>
                      <v-btn 
                        color="blue darken-1" 
                        text 
                        @click="deleteEnvItemConfirm"
                      >
                        OK
                      </v-btn>
                      <v-spacer />
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-toolbar>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-icon
                small
                class="mr-2"
                @click="editEnvItem(item)"
              >
                mdi-pencil
              </v-icon>
              <v-icon
                small
                @click="deleteEnvItem(item)"
              >
                mdi-delete
              </v-icon>
            </template>
            <template v-slot:no-data>
              <span>No environment variable configured</span>
            </template>
          </v-data-table>          
        </v-col>

        <!-- secrets -->

        <v-col 
          v-if="editedApp.Editable"
          cols="12"
        >
          <v-data-table
            :headers="[{text: 'Name', value: 'name'}, {text: 'Type', value: 'type'}, {text: 'Value', value: 'value'}, { text: 'Actions', value: 'actions', sortable: false },]"
            :items="secrets"
            sort-by="Name"
            class="elevation-1"
            hide-default-footer
          >
            <template v-slot:top>
              <v-toolbar
                flat
              >
                <v-toolbar-title>Secrets</v-toolbar-title>
                <v-divider
                  class="mx-4"
                  inset
                  vertical
                />
                <v-spacer />
                <v-dialog
                  v-model="secretDialog"
                  max-width="750px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      color="primary"
                      dark
                      icon
                      class="mb-2"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon 
                        dark
                        color="primary"
                      >
                        mdi-plus
                      </v-icon>
                    </v-btn>
                  </template>
                  <v-card>
                    <v-card-title>
                      <span class="text-h5">{{ secretVarformTitle }}</span>
                    </v-card-title>

                    <v-card-text>
                      <v-container>
                        <v-row>
                          <v-col
                            cols="12"
                            sm="4"
                            md="4"
                          >
                            <infinity-select
                              v-model="editedSecretVar.name"
                              :items="availableSecrets"
                              item-value="Name"
                              item-text="Name"
                              color="primary"
                              label="Name"
                            />
                          </v-col>
                          <v-col
                            cols="12"
                            sm="4"
                            md="4"
                          >
                            <infinity-select
                              v-model="editedSecretVar.type"
                              :items="['Mount', 'Environment']"
                              item-value="Type"
                              item-text="Type"
                              color="primary"
                              label="Type"
                            />
                          </v-col>                          
                          <v-col
                            cols="12"
                            sm="4"
                            md="4"
                          >
                            <v-text-field
                              v-model="editedSecretVar.value"
                              label="Value"
                            />
                          </v-col>                                                                            
                        </v-row>
                      </v-container>
                    </v-card-text>

                    <v-card-actions>
                      <v-spacer />
                      <v-btn
                        color="blue darken-1"
                        text
                        @click="closeSecret"
                      >
                        Cancel
                      </v-btn>
                      <v-btn
                        color="blue darken-1"
                        text
                        @click="saveSecret"
                      >
                        Save
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
                <v-dialog 
                  v-model="secretDialogDelete" 
                  max-width="500px"
                >
                  <v-card>
                    <v-card-title class="text-h5">
                      Are you sure you want to delete this secret?
                    </v-card-title>
                    <v-card-actions>
                      <v-spacer />
                      <v-btn 
                        color="blue darken-1" 
                        text 
                        @click="closeDeleteSecret"
                      >
                        Cancel
                      </v-btn>
                      <v-btn 
                        color="blue darken-1" 
                        text 
                        @click="deleteSecretItemConfirm"
                      >
                        OK
                      </v-btn>
                      <v-spacer />
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-toolbar>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-icon
                small
                class="mr-2"
                @click="editSecretItem(item)"
              >
                mdi-pencil
              </v-icon>
              <v-icon
                small
                @click="deleteSecretItem(item)"
              >
                mdi-delete
              </v-icon>
            </template>
            <template v-slot:no-data>
              <span>No secrets configured</span>
            </template>
          </v-data-table>          
        </v-col>
        <!-- /secrets-->
        <v-col 
          v-if="editedApp.Editable"
          cols="12"
        >
          <v-data-table
            :headers="[{text: 'Name', value: 'username'},{text: 'Role', value: 'role'}, { text: 'Actions', value: 'actions', sortable: false },]"
            :items="[]"
            sort-by="username"
            class="elevation-1"
            hide-default-footer
          >
            <template v-slot:top>
              <v-toolbar
                flat
              >
                <v-toolbar-title>Users</v-toolbar-title>
                <v-divider
                  class="mx-4"
                  inset
                  vertical
                />
                <v-spacer />
                <v-dialog
                  :value="false"
                  max-width="500px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      color="primary"
                      dark
                      icon
                      class="mb-2"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon 
                        dark
                        color="primary"
                      >
                        mdi-plus
                      </v-icon>
                    </v-btn>
                  </template>
                </v-dialog>
              </v-toolbar>              
            </template>
          </v-data-table>
        </v-col>

        <v-col
          v-if="editedApp.Editable"
          cols="12"
          class="text-right"
        >
          <v-btn
            v-if="!isNew && originalApp.State === 'AppRunning'"
            class="mr-4"
            color="error"
            min-width="150"
            @click="stop()"
          >
            Stop
          </v-btn>
          <v-btn
            v-if="!isNew && originalApp.State !== 'AppRunning'"
            class="mr-4"
            color="success"
            min-width="150"
            @click="start()"
          >
            Start
          </v-btn>
          <v-btn
            v-if="!isNew"
            :loading="appDeleteProgress"
            class="mr-4"
            color="error"
            min-width="150"
            @click="remove()"
          >
            Delete
          </v-btn>
          <v-btn
            v-if="!isNew"
            class="mr-4"
            color="success"
            min-width="150"
            @click="build()"
            v-html="buildText"
          />
          <v-btn
            color="primary"
            min-width="150"
            @click="isNew ? create() : update()"
            v-html="isNew ? 'Create App' : 'Update App'"
          />
        </v-col>
      </v-row>
    </v-container>
  </v-form>
</template>
<script>
  import axios from 'axios'
  export default {
    name: 'AppData',
    props: {
      app: {
        type: Object,
        default: () => ({}),
      },
    },
    data: () => {
      return {
        appDeleteProgress: false,
        availableStorages: [],
        availableSecrets: [],
        envVars: [],
        storages: [],
        secrets: [],
        isValid: false,
        editedApp: {},
        buildState: 'NoBuild',
        envDialog: false,
        envDialogDelete: false,
        editedEnvIndex: -1,
        storageDialog: false,
        storageDialogDelete: false,
        editedStorageIndex: -1,
        secretDialog: false,
        secretDialogDelete: false,
        editedSecretIndex: -1,
        dirty: false,
        editedEnvVar: {
          name: '',
          value: '',
        },
        defaultEnvVar: {
          name: '',
          value: '',
        },
        editedStorageVar: {
          source: '',
          target: '',
        },
        defaultStorageVar: {
          source: '',
          target: '',
        },
        editedSecretVar: {
          name: '',
          type: '',
          value: '',
        },
        defaultSecretVar: {
          name: '',
          type: '',
          value: '',
        },
      }
    },
    computed: {      
      appUrl () {
        return `https://${this.app.Name}-${this.app.UniverseName}.${location.host}`
      },
      envVarformTitle () {
        return this.editedEnvIndex === -1 ? 'New Variable' : 'Edit Variable'
      },
      storageVarformTitle () {
        return this.editedStorageIndex === -1 ? 'New Storage' : 'Edit Storage'
      },
      secretVarformTitle () {
        return this.editedSecretIndex === -1 ? 'New Secret' : 'Edit Secret'
      },
      appLanguages () {
        return ['Freestyle', ...Object.keys(this.$store.state.app.bundles)]
      },
      appTypes () {
        if (!this.editedApp.AppLanguage || this.editedApp.AppLanguage === 'Freestyle') {
          return []
        }
        return Object.keys(this.$store.state.app.bundles[this.editedApp.AppLanguage])
      },
      appVersions () {
        if (!this.editedApp.AppLanguage || this.editedApp.AppLanguage === 'Freestyle') {
          return []
        }
        if (!this.editedApp.AppType) {
          return []
        }
        return this.$store.state.app.bundles[this.editedApp.AppLanguage][this.editedApp.AppType].Versions
      },
      originalApp () {
        return this.$store.getters['app/app'](this.app.UniverseName, this.app.Name)
      },
      isNew () {
        if (!this.app?.Name) {
          return true
        }
        return false
      },
      universes () {
        const universes = []
        for (const universe of this.$store.state.app.universes) {
          if (universe.Name !== 'infinity') {
            universes.push(universe.Name)
          }
        }
        return universes
      },
      buildText () {
        if (this.buildState === 'NoBuild' && this.originalApp.State === 'AppRunning') {
          return 'Build & Deploy'
        }
        if (this.buildState === 'Building') {
          return 'Building...'
        }

        if (this.originalApp.State === 'AppDeploying') {
          return 'Deploying...'
        }

        return 'Build & Deploy'
      },
    },
    watch: {
      envDialog (val) {
        val || this.closeEnv()
      },
      envDialogDelete (val) {
        val || this.closeDeleteEnv()
      },
      originalApp (nV) {
        this.buildState = nV.BuildState

        if (!this.dirty) {
          try {
            const envVars = []
            const storages = []            
            const secrets = []
            const parsedVars = JSON.parse(nV.EnvVars || '[]')
            const parsedStorages = JSON.parse(nV.Storages || '[]')
            const parsedSecretEnvs = JSON.parse(nV.SecretEnvs || '[]')            
            const parsedSecretStorages = JSON.parse(nV.SecretStorages || '[]')
            for (const envVar in parsedVars) {
              envVars.push({
                name: envVar,
                value: parsedVars[envVar],
              })
            }
            for (const storage in parsedStorages) {
              storages.push({
                source: storage,
                target: parsedStorages[storage],
              })
            }
            for (const secretStorage in parsedSecretStorages) {
              secrets.push({
                name: secretStorage,
                type: 'Mount',
                value: parsedSecretStorages[secretStorage],
              })
            }
            for (const secretEnv in parsedSecretEnvs) {
              secrets.push({
                name: secretEnv,
                type: 'Environment',
                value: parsedSecretEnvs[secretEnv],
              })
            }
            this.envVars = envVars
            this.storages = storages
            this.secrets = secrets
          } catch (e) {
            this.envVars = []
            this.storages = []
            this.secrets = []
          }
        }
      },
      isNew (nV) {
        if (nV) {
          this.editedApp = {
            Name: '',
            Description: '',
            Editable: true,
            Port: 8080,
            Replicas: 1,
            EnvVars: '[]',
            Storages: '[]',
            Secrets: '[]',
          }
        } else {
          this.editedApp = { ...this.app }
          this.buildState = this.editedApp.BuildState
        }
      },
    },
    mounted () {
      const that = this
      const loader = async () => {
        that.availableStorages = await that.$store.dispatch('app/loadStorages', { name: that.$route.params.universeName })
        that.availableSecrets = await that.$store.dispatch('app/loadSecrets', { name: that.$route.params.universeName })
      }
      loader()

      this.$store.dispatch('app/loadBundles')      
      this.editedApp = {
        Name: '',
        Description: '',
        Editable: true,
        Port: 8080,
        Replicas: 1,
        EnvVars: '[]',
        Storages: '[]',
        Secrets: '[]',
      }
    },
    methods: {
      editEnvItem (item) {
        this.editedEnvIndex = this.envVars.indexOf(item)
        this.editedEnvVar = Object.assign({}, item)
        this.envDialog = true
      },
      editStorageItem (item) {
        this.editedStorageIndex = this.storages.indexOf(item)
        this.editedStorageVar = Object.assign({}, item)
        this.storageDialog = true
      },
      editSecretItem (item) {
        this.editedSecretIndex = this.secrets.indexOf(item)
        this.editedSecretVar = Object.assign({}, item)
        this.secretDialog = true
      },
      deleteEnvItem (item) {
        this.editedEnvIndex = this.envVars.indexOf(item)
        this.editedEnvVar = Object.assign({}, item)
        this.envDialogDelete = true
      },
      deleteStorageItem (item) {
        this.editedStorageIndex = this.storages.indexOf(item)
        this.editedStorageVar = Object.assign({}, item)
        this.storageDialogDelete = true
      },
      deleteSecretItem (item) {
        this.editedSecretIndex = this.storages.indexOf(item)
        this.editedSecretVar = Object.assign({}, item)
        this.secretDialogDelete = true
      },
      deleteEnvItemConfirm () {
        this.envVars.splice(this.editedEnvIndex, 1)
        this.dirty = true
        this.closeDeleteEnv()
      },
      deleteStorageItemConfirm () {
        this.storages.splice(this.editedStorageIndex, 1)
        this.dirty = true
        this.closeDeleteStorage()
      },
      deleteSecretItemConfirm () {
        this.secrets.splice(this.editedSecretIndex, 1)
        this.dirty = true
        this.closeDeleteSecret()
      },
      closeEnv () {
        this.envDialog = false
        this.$nextTick(() => {
          this.editedEnvVar = Object.assign({}, this.defaultEnvVar)
          this.editedEnvIndex = -1
        })
      },
      closeStorage () {
        this.storageDialog = false
        this.$nextTick(() => {
          this.editedStorageVar = Object.assign({}, this.defaultStorageVar)
          this.editedStorageIndex = -1
        })
      },
      closeSecret () {
        this.secretDialog = false
        this.$nextTick(() => {
          this.editedSecretVar = Object.assign({}, this.defaultSecretVar)
          this.editedSecretIndex = -1
        })
      },
      closeDeleteEnv () {
        this.envDialogDelete = false
        this.$nextTick(() => {
          this.editedEnvVar = Object.assign({}, this.defaultEnvVar)
          this.editedEnvIndex = -1
        })
      },
      closeDeleteStorage () {
        this.storageDialogDelete = false
        this.$nextTick(() => {
          this.editedStorageVar = Object.assign({}, this.defaultStorageVar)
          this.editedStorageIndex = -1
        })
      },
      closeDeleteSecret () {
        this.secretDialogDelete = false
        this.$nextTick(() => {
          this.editedSecretVar = Object.assign({}, this.defaultSecretVar)
          this.editedSecretIndex = -1
        })
      },
      saveEnv () {
        if (this.editedEnvIndex > -1) {
          Object.assign(this.envVars[this.editedEnvIndex], this.editedEnvVar)
        } else {
          this.envVars.push(this.editedEnvVar)
        }
        this.dirty = true
        this.closeEnv()
      },
      saveStorage () {
        if (this.editedStorageIndex > -1) {
          Object.assign(this.storages[this.editedStorageIndex], this.editedStorageVar)
        } else {
          this.storages.push(this.editedStorageVar)
        }
        this.dirty = true
        this.closeStorage()
      },
      saveSecret () {
        if (this.editedSecretIndex > -1) {
          Object.assign(this.secrets[this.editedSecretIndex], this.editedSecretVar)
        } else {
          this.secrets.push(this.editedSecretVar)
        }
        this.dirty = true
        this.closeSecret()
      },
      create () {
        if (!this.isValid) {
          return
        }
        const vars = {}
        const storages = {}        
        this.envVars.forEach(envVar => {
          vars[envVar.name] = envVar.value
        })        
        this.storages.forEach(storage => {
          storages[storage.source] = storage.target
        })
        const secretsEnvs = {}
        const secretsStorages = {}
        this.secrets.forEach(secret => {
          if (secret.type === 'mount') {
            secretsStorages[secret.name] = secret.value
          } else {
            secretsEnvs[secret.name] = secret.value
          }
        })
        const newdApp = {
          ...this.editedApp,
          EnvVars: JSON.stringify(vars),
          Storages: JSON.stringify(storages),
          SecretStorages: JSON.stringify(secretsStorages),
          SecretEnvs: JSON.stringify(secretsEnvs),
        }
        const store = this.$store
        const router = this.$router
        axios
          .post(`/api/universes/get/${this.editedApp.UniverseName}/apps/add`, newdApp)
          .then(async response => {
            if (response.status === 201) {
              await store.dispatch('app/loadUniverses')
              store.commit('app/feedbackMessage', `App ${this.editedApp.UniverseName}/${this.editedApp.Name} created.`)
              store.commit('app/feedbackColor', 'success')
              store.commit('app/displayFeedback', true)
              this.buildState = 'Building'
              router.push({ name: 'App', params: { universeName: this.editedApp.UniverseName, appName: this.editedApp.Name } })
            } else {
              store.commit('app/feedbackMessage', `Something went wrong creating app ${this.editedApp.UniverseName}/${this.editedApp.Name}!`)
              store.commit('app/feedbackColor', 'error')
              store.commit('app/displayFeedback', true)
            }
          })
      },
      update () {
        if (!this.isValid) {
          return
        }
        const vars = {}
        const storages = {}
        this.envVars.forEach(envVar => {
          vars[envVar.name] = envVar.value
        })
        this.storages.forEach(storage => {
          storages[storage.source] = storage.target
        })
        const secretsEnvs = {}
        const secretsStorages = {}
        this.secrets.forEach(secret => {
          if (secret.type === 'Mount') {
            secretsStorages[secret.name] = secret.value
          } else {
            secretsEnvs[secret.name] = secret.value
          }
        })
        const newdApp = {
          ...this.editedApp,
          EnvVars: JSON.stringify(vars),
          Storages: JSON.stringify(storages),
          SecretStorages: JSON.stringify(secretsStorages),
          SecretEnvs: JSON.stringify(secretsEnvs),
        }
        
        axios
          .put(`/api/universes/get/${this.editedApp.UniverseName}/apps/get/${this.editedApp.Name}/`, newdApp)
          .then(response => {
            if (response.status === 201) {
              this.$store.commit('app/feedbackMessage', `App ${this.editedApp.UniverseName}/${this.editedApp.Name} updated.`)
              this.$store.commit('app/feedbackColor', 'success')
              this.$store.commit('app/displayFeedback', true)
            } else {
              this.$store.commit('app/feedbackMessage', `Something went wrong creating app ${this.editedApp.UniverseName}/${this.editedApp.Name}!`)
              this.$store.commit('app/feedbackColor', 'error')
              this.$store.commit('app/displayFeedback', true)
            }
          })
      },
      remove () {
        const store = this.$store
        const router = this.$router
        this.appDeleteProgress = true
        axios
          .delete(`/api/universes/get/${this.editedApp.UniverseName}/apps/get/${this.editedApp.Name}/`)
          .then(async response => {
            if (response.status === 200) {
              await store.dispatch('app/loadUniverses')
              this.$store.commit('app/feedbackMessage', `App ${this.editedApp.UniverseName}/${this.editedApp.Name} deleted.`)
              this.$store.commit('app/feedbackColor', 'error')
              this.$store.commit('app/displayFeedback', true)
              router.push({ name: 'Universe', params: { universeName: this.editedApp.UniverseName } })
              this.appDeleteProgress = false
            } else {
              this.$store.commit('app/feedbackMessage', `Something went wrong deleting app ${this.editedApp.UniverseName}/${this.editedApp.Name}!`)
              this.$store.commit('app/feedbackColor', 'error')
              this.$store.commit('app/displayFeedback', true)
              this.appDeleteProgress = false
            }
          })
      },
      stop () {
        const store = this.$store
        axios
          .post(`/api/universes/get/${this.editedApp.UniverseName}/apps/get/${this.editedApp.Name}/stop`)
          .then(async response => {
            if (response.status === 200) {
              await store.dispatch('app/loadUniverses')
              this.$store.commit('app/feedbackMessage', `App ${this.editedApp.UniverseName}/${this.editedApp.Name} stopped.`)
              this.$store.commit('app/feedbackColor', 'success')
              this.$store.commit('app/displayFeedback', true)
            } else {
              this.$store.commit('app/feedbackMessage', `Something went wrong stopping app ${this.editedApp.UniverseName}/${this.editedApp.Name}!`)
              this.$store.commit('app/feedbackColor', 'error')
              this.$store.commit('app/displayFeedback', true)
            }
          })
      },
      start () {
        const store = this.$store
        axios
          .post(`/api/universes/get/${this.editedApp.UniverseName}/apps/get/${this.editedApp.Name}/start`)
          .then(async response => {
            if (response.status === 200) {
              await store.dispatch('app/loadUniverses')
              this.$store.commit('app/feedbackMessage', `App ${this.editedApp.UniverseName}/${this.editedApp.Name} started.`)
              this.$store.commit('app/feedbackColor', 'success')
              this.$store.commit('app/displayFeedback', true)
            } else {
              this.$store.commit('app/feedbackMessage', `Something went wrong starting app ${this.editedApp.UniverseName}/${this.editedApp.Name}!`)
              this.$store.commit('app/feedbackColor', 'error')
              this.$store.commit('app/displayFeedback', true)
            }
          })
      },
      build () {
        if (!this.isValid) {
          return
        }
        axios
          .post(`/api/universes/get/${this.editedApp.UniverseName}/apps/get/${this.editedApp.Name}/build`)
          .then(async response => {
            if (response.status === 200) {
              await this.$store.dispatch('app/loadUniverses')
              this.$store.commit('app/feedbackMessage', `App ${this.editedApp.UniverseName}/${this.editedApp.Name} started to build.`)
              this.$store.commit('app/feedbackColor', 'success')
              this.$store.commit('app/displayFeedback', true)
            } else {
              this.$store.commit('app/feedbackMessage', `Something went wrong building app ${this.editedApp.UniverseName}/${this.editedApp.Name}!`)
              this.$store.commit('app/feedbackColor', 'error')
              this.$store.commit('app/displayFeedback', true)
            }
          })
      },
    },
  }
</script>
